import { Box, Button, Typography } from '@mui/material';
import { FlashlightBenchmarkGrid } from '../../components/FlashlightBenchmarkGrid';
import { gridModel, type GridYear, type SchemeLevel } from '../../components/flashlightGrid';
import { PlanCard } from './components';
import { getDistrictConfig } from './districtConfig';
import { FLASHLIGHT_VALUE_MESSAGE } from './flashlight';
import type { FlashlightBenchmarksResponse } from './flashlightBenchmarks';
import type { PlanFlashlight } from './planRecordApi';
import { formatPlanDate } from './planRecordView';
import { COLORS } from './tokens';

export type PlanBenchmarksSource =
  | { kind: 'live'; data: FlashlightBenchmarksResponse | null; failed: boolean }
  | { kind: 'frozen'; data: PlanFlashlight }
  | { kind: 'asOf'; asOf: string; data: FlashlightBenchmarksResponse | null; failed: boolean };

const DEFAULT_SCHEME: SchemeLevel[] = [
  { score: 1, label: 'Limited', color: '#F86464' },
  { score: 2, label: 'Emerging', color: '#FBDA67' },
  { score: 3, label: 'Expanding', color: '#2BCC57' },
  { score: 4, label: 'Bridging', color: '#0094FF' },
  { score: 5, label: 'Advanced', color: '#7270FE' },
];

const NOT_SAVED = 'Flashlight 360 benchmarks weren’t saved with this plan.';
const LATER = 'Later benchmarks, and Flashlight 360 assignments, are on the student profile, not on this plan.';
const OUTAGE = 'Benchmarks could not be loaded. Refresh the page to try again.';

const textSx = { fontSize: 14, fontWeight: 500, lineHeight: 1.57, color: COLORS.ink } as const;
const mutedSx = { fontSize: 16, fontWeight: 500, color: COLORS.muted } as const;

function planYearEnd(planYear: string): number {
  const start = Number(planYear.slice(0, 4));
  return Number.isFinite(start) ? start + 1 : new Date().getUTCFullYear();
}

function ProfileLink({ onOpenProfile }: { onOpenProfile: () => void }) {
  return (
    <Button onClick={onOpenProfile} sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0, lineHeight: '24px', color: COLORS.link }}>
      Current scores are on the student profile
    </Button>
  );
}

function Grid({ years, scheme, planYear }: { years: GridYear[]; scheme: SchemeLevel[] | undefined; planYear: string }) {
  const model = gridModel(years, planYearEnd(planYear));
  if (!model.hasData) return <Typography sx={mutedSx}>No benchmarks scored yet.</Typography>;
  return <FlashlightBenchmarkGrid model={model} scheme={scheme?.length ? scheme : DEFAULT_SCHEME} />;
}

function sourceFailed(data: FlashlightBenchmarksResponse | null): boolean {
  return data != null && data.sources.some((source) => source.status === 'error' || source.status === 'skipped');
}

function Body({ source, planYear, onOpenProfile }: { source: PlanBenchmarksSource; planYear: string; onOpenProfile: () => void }) {
  if (source.kind === 'live') {
    if (source.failed || sourceFailed(source.data)) return <Typography sx={textSx}>{OUTAGE}</Typography>;
    if (!source.data) return null;
    return <Grid years={source.data.years} scheme={source.data.scheme} planYear={planYear} />;
  }
  const asOf = source.kind === 'frozen' ? source.data.asOf : source.asOf;
  const failed = source.kind === 'frozen' ? source.data.status !== 'saved' : source.failed || sourceFailed(source.data) || source.data?.rubricUncertain === true;
  const saved = !failed && (source.kind === 'frozen' || source.data != null);
  if (!saved && !failed) return null;
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <Typography sx={mutedSx}>{failed ? NOT_SAVED : `As of ${formatPlanDate(asOf)}, when this plan was finalized.`}</Typography>
        <ProfileLink onOpenProfile={onOpenProfile} />
      </Box>
      {saved && source.kind === 'frozen' && <Grid years={source.data.years} scheme={source.data.scheme} planYear={planYear} />}
      {saved && source.kind === 'asOf' && <Grid years={source.data!.years} scheme={source.data!.scheme} planYear={planYear} />}
      {saved && <Typography sx={mutedSx}>{LATER}</Typography>}
    </>
  );
}

export function FlashlightBenchmarksCard({
  planYear,
  source,
  onOpenProfile,
  subtitle,
  usesFlashlight = getDistrictConfig().usesFlashlight,
}: {
  planYear: string;
  source: PlanBenchmarksSource;
  onOpenProfile: () => void;
  subtitle?: string;
  usesFlashlight?: boolean;
}) {
  if (!usesFlashlight) {
    return (
      <PlanCard title="Flashlight 360 benchmarks">
        <Typography sx={textSx}>{FLASHLIGHT_VALUE_MESSAGE}</Typography>
      </PlanCard>
    );
  }
  return (
    <PlanCard title="Flashlight 360 benchmarks" subtitle={subtitle}>
      <Body source={source} planYear={planYear} onOpenProfile={onOpenProfile} />
    </PlanCard>
  );
}
